import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, useLocation, useParams } from 'react-router-dom'
import { CheckCircle2, ShieldCheck, Tag, X } from 'lucide-react'
import InvoiceInfoForm, { AddressFields } from '@/components/InvoiceInfoForm'
import PaymentMethodPicker from '@/components/PaymentMethodPicker'
import RecipientEmailInput from '@/components/RecipientEmailInput'
import { emptyRow, type EmailRow } from '@/lib/recipientRow'
import { emptyInvoiceInfo, type InvoiceInfo } from '@/types/invoiceInfo'
import { useAuthStore } from '@/store/authStore'
import { createCartOrder, payOrder, type Order, type ParticipantInput } from '@/services/orderService'
import { MAX_BOOK_QUANTITY, MAX_ONLINE_QUANTITY, previewCart, toOrderItems, useCart, type CartKind, type CartLine, type CartPreview } from '@/services/cartService'
import { getProfile } from '@/services/profileService'
import applyBg from '@/pages/JobApplication/assets/apply-bg.png'
import { filterPhone, isValidPhone, isValidTaxId } from '@/lib/inputFilters'

// หน้าชำระเงินหน้าเดียวของระบบ (คอร์สออนไลน์/หนังสือ) — /checkout/online หรือ /checkout/books
// เข้ามาได้ 2 ทาง: (1) ปุ่ม "สั่งซื้อ/ลงทะเบียน" ที่หน้าสินค้า → ซื้อชิ้นนั้นชิ้นเดียวทันที (state.direct ไม่ผ่านตะกร้า)
// (2) ปุ่ม "ดำเนินการชำระเงิน" ที่หน้าตะกร้า → ซื้อทั้งแท็บ · ปุ่ม "เพิ่มลงตะกร้า" แค่ใส่ตะกร้า ไม่มาหน้านี้
// สร้างคำสั่งซื้อ 1 ใบ แล้วจ่ายต่อทันที (ราคา/สต๊อก/กติกาคำนวณและตรวจที่ API) · ใส่คูปองและปรับจำนวนได้ในหน้านี้
// หนังสือ: ขอที่อยู่จัดส่ง · บัญชีบริษัทซื้อคอร์สออนไลน์: กรอกอีเมลพนักงานต่อคอร์ส (จำนวนช่อง = จำนวนพนักงานที่ตั้งในตะกร้า)
// ชำระเงิน: ONLINE ต่อ Payment Gateway จริง (redirect ไปหน้า TEP เลือกช่องทางจ่ายที่นั่น → กลับ /payment/result/:orderId) · FREE ส่งมอบทันที

const inputClass =
  'w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted/70 outline-none transition focus:border-indigo-400'

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-sm border border-border bg-surface p-6 shadow-sm">
      <h2 className="mb-1 border-l-4 border-indigo-500 pl-3 text-xl font-semibold text-text">{title}</h2>
      {subtitle && <p className="mb-5 pl-3 text-xs text-text-muted">{subtitle}</p>}
      {!subtitle && <div className="mb-4" />}
      {children}
    </div>
  )
}

// สินค้าที่กดซื้อเลยจากหน้าสินค้า (ไม่ผ่านตะกร้า)
export interface DirectItem {
  kind: CartKind
  refId: number
  quantity: number
  title: string
  image?: string | null
  subtitle?: string | null
}

export default function CartCheckoutPage() {
  const { kind } = useParams()
  const isBooks = kind === 'books'
  const validKind = kind === 'books' || kind === 'online'
  const location = useLocation()
  const navState = location.state as { couponCode?: string | null; direct?: DirectItem } | null
  const direct = navState?.direct && navState.direct.kind === (isBooks ? 'book' : 'online') ? navState.direct : null

  const customerType = useAuthStore((s) => s.customerType)
  const isCompany = customerType === 'COMPANY'
  const cart = useCart()
  const [directQty, setDirectQty] = useState(direct?.quantity ?? 1)
  const cartLines = cart.lines
  const lines = useMemo<CartLine[]>(
    () => direct
      ? [{ cartItemId: 0, kind: direct.kind, refId: direct.refId, quantity: directQty, title: direct.title, slug: null, price: 0, image: direct.image ?? null, subtitle: direct.subtitle ?? null, available: true }]
      : cartLines.filter((l) => l.kind === (isBooks ? 'book' : 'online')),
    [direct, directQty, isBooks, cartLines],
  )
  const [couponCode, setCouponCode] = useState<string | null>(navState?.couponCode ?? null)
  const [couponInput, setCouponInput] = useState('')
  const canEditQty = !!direct && (isBooks || isCompany)   // ซื้อเลย: หนังสือปรับจำนวนเล่ม / บริษัทปรับจำนวนพนักงานได้ที่นี่ (จากตะกร้าปรับที่หน้าตะกร้า)
  const hasBooks = isBooks
  const needsEmployees = isCompany && !isBooks

  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [shipping, setShipping] = useState<InvoiceInfo>(() => emptyInvoiceInfo('INDIVIDUAL', false))
  const [shipAddress, setShipAddress] = useState('')
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo>(() => emptyInvoiceInfo(isCompany ? 'COMPANY' : 'INDIVIDUAL', isCompany))
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [paidOrder, setPaidOrder] = useState<Order | null>(null)
  // อีเมลพนักงานต่อคอร์ส (บริษัท) — เก็บตาม course_id; จำนวนช่องปรับตามจำนวนพนักงานในตะกร้าเสมอ
  const [emailRows, setEmailRows] = useState<Record<number, EmailRow[]>>({})
  const rowsOf = (refId: number, quantity: number): EmailRow[] => {
    const cur = emailRows[refId] ?? []
    return Array.from({ length: quantity }, (_, i) => cur[i] ?? emptyRow())
  }
  const setRow = (refId: number, quantity: number, index: number, next: EmailRow) =>
    setEmailRows((prev) => ({ ...prev, [refId]: rowsOf(refId, quantity).map((r, i) => (i === index ? next : r)) }))

  // เติมข้อมูลจากโปรไฟล์ให้ล่วงหน้า (แก้ได้) เหมือนหน้าสั่งซื้อหนังสือเดิม
  useEffect(() => {
    let cancelled = false
    getProfile()
      .then((p) => {
        if (cancelled) return
        if ('company_name' in p) {
          setInvoiceInfo((prev) => ({ ...prev, companyName: prev.companyName || p.company_name || '', taxId: prev.taxId || p.tax_id || '' }))
        } else {
          setFullName((prev) => prev || `${p.first_name} ${p.last_name}`.trim())
          setPhone((prev) => prev || p.phone || '')
          setShipping((prev) => ({ ...prev, province: prev.province || p.province || '' }))
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // ตรวจตะกร้ากับ API (ราคาจริง/ของหมด/เรียนอยู่แล้ว) — เก็บผลคู่กับกุญแจคำขอ กำลังโหลด = ผลที่ถืออยู่เป็นของคำขอเก่า
  const requestKey = useMemo(() => JSON.stringify([kind, couponCode, lines.map((l) => [l.refId, l.quantity])]), [kind, couponCode, lines])
  const [result, setResult] = useState<{ key: string; preview: CartPreview | null; error: string } | null>(null)
  const preview = result?.key === requestKey ? result.preview : null
  const loadError = result?.key === requestKey ? result.error : ''
  const loading = lines.length > 0 && result?.key !== requestKey
  useEffect(() => {
    if (!validKind || lines.length === 0 || paidOrder) return
    let cancelled = false
    previewCart(lines, couponCode)
      .then((p) => { if (!cancelled) setResult({ key: requestKey, preview: p, error: '' }) })
      .catch((e: unknown) => { if (!cancelled) setResult({ key: requestKey, preview: null, error: e instanceof Error ? e.message : 'ตรวจสอบตะกร้าไม่สำเร็จ' }) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, paidOrder])

  if (paidOrder) {
    const boughtBooks = paidOrder.items.some((it) => it.item_type === 'BOOK')
    const boughtCourses = paidOrder.items.some((it) => it.item_type === 'COURSE')
    return (
      <div className="relative isolate flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#FAFAFB] px-4 pb-16 pt-24 text-center">
        <img src={applyBg} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
        <CheckCircle2 className="relative mb-4 h-14 w-14 text-emerald-500" />
        <h1 className="relative text-2xl font-bold text-text">ชำระเงินสำเร็จ</h1>
        <p className="relative mt-1 text-sm text-text-muted">เลขที่คำสั่งซื้อ {paidOrder.order_number}</p>
        <ul className="relative mt-4 max-w-md space-y-1 text-sm text-text-muted">
          {paidOrder.items.map((it) => (
            <li key={it.order_item_id}>{it.item_name}{it.quantity > 1 ? ` × ${it.quantity}` : ''}</li>
          ))}
        </ul>
        <p className="relative mt-3 text-sm font-semibold text-text">ยอดรวม {paidOrder.final_amount <= 0 ? 'ฟรี' : `฿${paidOrder.final_amount.toLocaleString()}`}{boughtBooks ? ' (หนังสือส่งฟรี)' : ''}</p>
        {boughtBooks && paidOrder.shipping && (
          <p className="relative mt-3 max-w-md rounded-sm border border-border bg-surface px-4 py-3 text-xs leading-relaxed text-text-muted">
            จัดส่งหนังสือถึง {paidOrder.shipping.recipient_name} · {paidOrder.shipping.phone}<br />
            {paidOrder.shipping.address} {paidOrder.shipping.subdistrict} {paidOrder.shipping.district} {paidOrder.shipping.province} {paidOrder.shipping.postal_code}
          </p>
        )}
        <div className="relative mt-6 flex flex-wrap justify-center gap-3">
          {boughtCourses && !isCompany && (
            <Link to="/account?tab=courses" className="rounded-full bg-indigo-500 px-8 py-3 text-sm font-semibold text-white hover:bg-violet-400">ไปที่คอร์สเรียนของฉัน</Link>
          )}
          <Link to="/account?tab=orders" className={`rounded-full px-8 py-3 text-sm font-semibold ${boughtCourses && !isCompany ? 'border border-border text-text hover:border-accent/40' : 'bg-indigo-500 text-white hover:bg-violet-400'}`}>
            ดูประวัติคำสั่งซื้อ
          </Link>
        </div>
      </div>
    )
  }

  if (!validKind || (!direct && !cart.loaded)) return validKind ? <p className="pt-32 text-center text-sm text-text-muted">กำลังโหลด...</p> : <Navigate to="/cart" replace />
  if (lines.length === 0) return <Navigate to="/cart" replace />

  const total = preview?.final_amount ?? 0
  const payZero = !!preview && total <= 0

  // ข้อมูลผู้รับใบกำกับภาษีต้องครบเมื่อ: บัญชีบริษัท / เลือกนิติบุคคล / ติ๊ก "ต้องการใบกำกับภาษี" (เหมือนหน้าซื้อทันที)
  const needsInvoiceInfo = isCompany || invoiceInfo.recipientType === 'COMPANY' || invoiceInfo.wantsInvoice
  const invoiceAddressOk = !!(invoiceInfo.address.trim() && invoiceInfo.province && invoiceInfo.district.trim() && invoiceInfo.subdistrict.trim() && invoiceInfo.postalCode.trim())
  const invoiceInfoValid = !needsInvoiceInfo || (
    invoiceInfo.recipientType === 'COMPANY'
      ? !!(invoiceInfo.companyName.trim() && isValidTaxId(invoiceInfo.taxId)) && invoiceAddressOk
      : !!(invoiceInfo.fullName.trim() && isValidTaxId(invoiceInfo.taxId)) && invoiceAddressOk
  )

  const submit = async () => {
    if (!preview || !preview.can_checkout) return setError('มีรายการที่สั่งซื้อไม่ได้ กรุณาตรวจสอบรายการด้านบน')
    if (needsEmployees) {
      for (const l of lines) {
        const rows = rowsOf(l.refId, l.quantity)
        if (rows.some((r) => !r.email.trim() || r.status !== 'ok')) return setError(`กรุณากรอกอีเมลพนักงานของคอร์ส "${l.title}" ให้ครบและถูกต้องทุกคน`)
      }
    }
    if (hasBooks) {
      if (!fullName.trim() || !phone.trim()) return setError('กรุณากรอกชื่อและเบอร์โทรศัพท์ผู้รับ')
      if (!isValidPhone(phone.trim())) return setError('เบอร์โทรศัพท์ต้องเป็นตัวเลข 9–10 หลัก ขึ้นต้นด้วย 0')
      if (!shipAddress.trim() || !shipping.province || !shipping.district || !shipping.subdistrict || !shipping.postalCode) {
        return setError('กรุณากรอกที่อยู่จัดส่งให้ครบ')
      }
    }
    if (!invoiceInfoValid) return setError('กรุณากรอกข้อมูลผู้สั่งซื้อสำหรับใบกำกับภาษีให้ครบ (เลขประจำตัวผู้เสียภาษี 13 หลัก)')
    setError('')
    setSubmitting(true)
    try {
      const order = await createCartOrder(toOrderItems(lines), preview.coupon?.code)
      // รายชื่อพนักงานแยกตามรายการ (order_item_id) — จับคู่จาก course_id ของแต่ละรายการในออเดอร์
      let participants: Record<string, ParticipantInput[]> | undefined
      if (needsEmployees) {
        participants = {}
        for (const item of order.items) {
          const line = lines.find((l) => item.item_type === 'COURSE' && l.refId === item.ref_id)
          if (line) participants[String(item.order_item_id)] = rowsOf(line.refId, line.quantity).map((r) => ({ email: r.email.trim().toLowerCase() }))
        }
      }
      const paid = await payOrder(
        order.order_id, payZero ? 'FREE' : 'ONLINE', participants, invoiceInfo,
        hasBooks
          ? {
              recipient_name: fullName.trim(), phone: phone.trim(), address: shipAddress.trim(),
              province: shipping.province, district: shipping.district.trim(),
              subdistrict: shipping.subdistrict.trim(), postal_code: shipping.postalCode.trim(),
            }
          : undefined,
      )
      if (paid.redirect_url) {
        // จ่ายออนไลน์: order ยังเป็น PENDING — พาลูกค้าไปหน้าจ่ายของ TEP แล้ว TEP พากลับมาที่ /payment/result/:orderId (ตะกร้าล้างตอนยืนยันแล้ว)
        window.location.assign(paid.redirect_url)
        return
      }
      setPaidOrder(paid)
      void cart.refresh()   // จ่ายสำเร็จ → server ลบรายการที่ซื้อออกจากตะกร้าให้แล้ว ดึงตะกร้าใหม่ (ล้มเหลวต้องไม่ทำของหาย)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ชำระเงินไม่สำเร็จ')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAFB] pb-16 pt-24">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <h1 className="mb-6 font-display text-2xl font-bold text-text">{direct ? 'ชำระเงิน' : isBooks ? 'ชำระเงิน — หนังสือในตะกร้า' : 'ชำระเงิน — คอร์สออนไลน์ในตะกร้า'}</h1>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
          <div className="space-y-6">
            <Section title="รายการสั่งซื้อ">
              <ul className="divide-y divide-border">
                {lines.map((l) => {
                  const info = preview?.items.find((p) => p.ref_id === l.refId && p.item_type === (l.kind === 'book' ? 'BOOK' : 'COURSE'))
                  return (
                    <li key={`${l.kind}-${l.refId}`} className="flex items-center gap-4 py-3 first:pt-0 last:pb-0">
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-sm border border-border bg-surface-2">
                        {l.image ? <img src={l.image} alt="" className="h-full w-full object-cover" /> : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium leading-snug text-text">{l.title}</p>
                        <p className="text-xs text-text-muted">
                          {l.kind === 'book' ? `หนังสือ · ${l.quantity} เล่ม` : isCompany ? `คอร์สออนไลน์ · ${l.quantity} คน` : 'คอร์สออนไลน์'}
                        </p>
                        {canEditQty && (
                          <div className="mt-2 inline-flex items-center rounded-sm border border-border text-sm">
                            <button type="button" onClick={() => setDirectQty((q) => Math.max(1, q - 1))} className="h-8 w-8 text-text-muted hover:text-accent" aria-label="ลดจำนวน">−</button>
                            <span className="w-10 text-center font-semibold text-text">{directQty}</span>
                            <button type="button" onClick={() => setDirectQty((q) => Math.min(isBooks ? MAX_BOOK_QUANTITY : MAX_ONLINE_QUANTITY, q + 1))} className="h-8 w-8 text-text-muted hover:text-accent" aria-label="เพิ่มจำนวน">+</button>
                          </div>
                        )}
                        {info && !info.ok && <p className="text-xs text-rose-500">{info.error}</p>}
                      </div>
                      <p className="shrink-0 font-semibold text-text">{info ? (info.is_free ? 'ฟรี' : `฿${(info.subtotal ?? 0).toLocaleString()}`) : '...'}</p>
                    </li>
                  )
                })}
              </ul>
            </Section>

            {needsEmployees && lines.map((l) => (
              <Section key={l.refId} title={`พนักงานที่จะได้คอร์ส: ${l.title}`} subtitle={`พิมพ์อีเมลที่พนักงานใช้สมัครสมาชิก CoWork แล้ว ${l.quantity} คน — คอร์สจะเข้าบัญชีของแต่ละคนหลังชำระเงิน (${direct ? 'ปรับจำนวนได้ที่รายการสั่งซื้อด้านบน' : 'เปลี่ยนจำนวนที่หน้าตะกร้า'})`}>
                <div className="space-y-3">
                  {rowsOf(l.refId, l.quantity).map((row, i, all) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-surface-2 text-xs font-semibold text-text-muted">{i + 1}</span>
                      <div className="flex-1">
                        <RecipientEmailInput
                          value={row}
                          onChange={(next) => setRow(l.refId, l.quantity, i, next)}
                          courseId={l.refId}
                          takenEmails={all.filter((r, idx) => idx !== i && r.status === 'ok').map((r) => r.email.trim().toLowerCase())}
                          requireAccount
                          lookup
                          withIcon
                          placeholder="พิมพ์อีเมลพนักงาน เช่น name@email.com"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            ))}

            {hasBooks && (
              <Section title="ข้อมูลผู้รับและที่อยู่จัดส่ง" subtitle="จัดส่งหนังสือทุกเล่มในตะกร้าไปยังที่อยู่นี้ (ส่งฟรี)">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-sm text-text-muted">ชื่อ-นามสกุลผู้รับ</label>
                    <input className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm text-text-muted">เบอร์โทรศัพท์</label>
                    <input className={inputClass} value={phone} onChange={(e) => setPhone(filterPhone(e.target.value))} inputMode="tel" maxLength={10} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="mb-1.5 block text-sm text-text-muted">ที่อยู่</label>
                    <input className={inputClass} value={shipAddress} onChange={(e) => setShipAddress(e.target.value)} placeholder="บ้านเลขที่ หมู่ ถนน ซอย อาคาร" />
                  </div>
                  <AddressFields info={shipping} onChange={setShipping} />
                </div>
              </Section>
            )}

            <InvoiceInfoForm isCompany={isCompany} info={invoiceInfo} onChange={setInvoiceInfo} />

            {!payZero && (
              <Section title="วิธีการชำระเงิน">
                <PaymentMethodPicker />
              </Section>
            )}
          </div>

          <div>
            <div className="sticky top-24 space-y-4">
              <div className="overflow-hidden rounded-sm border border-border bg-surface shadow-sm">
                <div className="border-b border-border bg-indigo-500/10 px-5 py-3 font-semibold text-text">สรุปการสั่งซื้อ</div>
                <div className="p-5 text-sm">
                  {loadError && <p className="mb-3 text-xs text-rose-500">{loadError}</p>}
                  {preview?.cart_error && <p className="mb-3 text-xs text-rose-500">{preview.cart_error}</p>}
                  <div className="mb-2 flex justify-between text-text-muted">
                    <span>ยอดรวม ({lines.length} รายการ)</span><span>{loading ? '...' : `฿${(preview?.subtotal ?? 0).toLocaleString()}`}</span>
                  </div>
                  <div className="my-3">
                    {preview?.coupon ? (
                      <div className="flex items-center justify-between gap-3 rounded-sm border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-sm">
                        <span className="flex min-w-0 items-center gap-2 text-emerald-700">
                          <Tag className="h-4 w-4 shrink-0" />
                          <span className="truncate">คูปอง <b>{preview.coupon.code}</b> · ลด ฿{preview.discount.toLocaleString()}</span>
                        </span>
                        <button type="button" onClick={() => { setCouponCode(null); setCouponInput('') }} className="shrink-0 rounded p-1 text-emerald-700 hover:bg-emerald-100" aria-label="ลบคูปอง"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1.5 block text-sm text-text-muted">มีคูปองส่วนลด?</label>
                        <div className="flex gap-2">
                          <input
                            value={couponInput}
                            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (couponInput.trim()) setCouponCode(couponInput.trim()) } }}
                            placeholder="กรอกรหัสคูปอง"
                            maxLength={30}
                            className="w-full rounded-sm border border-border bg-surface-2 px-3.5 py-2.5 text-sm uppercase text-text outline-none transition placeholder:normal-case placeholder:text-text-muted/70 focus:border-indigo-400"
                          />
                          <button type="button" onClick={() => { if (couponInput.trim()) setCouponCode(couponInput.trim()) }} className="shrink-0 rounded-sm border border-border px-4 text-sm font-medium text-text hover:border-accent/40">ใช้</button>
                        </div>
                        {preview?.coupon_error && <p className="mt-1.5 text-xs text-rose-500">{preview.coupon_error}</p>}
                      </div>
                    )}
                  </div>
                  {hasBooks && (
                    <div className="mb-2 flex justify-between text-text-muted">
                      <span>ค่าจัดส่งหนังสือ</span><span className="font-medium text-emerald-600">ฟรี</span>
                    </div>
                  )}
                  <div className="my-3 border-t border-dashed border-border" />
                  <div className="mb-4 flex justify-between font-bold text-text">
                    <span>สรุปยอดชำระ</span><span>{loading ? '...' : (payZero ? 'ฟรี' : `฿${total.toLocaleString()}`)}</span>
                  </div>
                  {error && <p className="mb-3 text-xs text-rose-500">{error}</p>}
                  <button
                    onClick={submit}
                    disabled={submitting || loading || !preview?.can_checkout}
                    className="flex w-full items-center justify-center rounded-full bg-indigo-500 py-3 font-semibold text-white transition hover:bg-violet-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {submitting ? 'กำลังดำเนินการ...' : 'ชำระเงิน'}
                  </button>
                </div>
              </div>
              <div className="rounded-sm border border-border bg-surface p-4 shadow-sm">
                <div className="flex items-center gap-2 text-sm font-medium text-text-muted">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-emerald-400" />
                  ชำระเงินปลอดภัย
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-text-muted">ข้อมูลของคุณถูกเข้ารหัสและใช้เพื่อการสั่งซื้อเท่านั้น</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
